import { format } from 'date-fns';
import { SNOOZABLE_TASK_STATUSES, isActivelySnoozed } from '@erp/shared-types';
import {
  fromDateTimeLocalValue,
  isSnoozeInstantInPast,
  snoozePresetUntil,
  toDateTimeLocalValue,
} from '@/lib/snoozeClock';

export { isActivelySnoozed };
export {
  fromDateTimeLocalValue,
  isSnoozeInstantInPast,
  snoozePresetUntil,
  toDateTimeLocalValue,
};

const SNOOZABLE = new Set<string>(SNOOZABLE_TASK_STATUSES);

export function isSnoozableTaskStatus(status: string): boolean {
  return SNOOZABLE.has(status);
}

/**
 * Search turns the queue's "show snoozed" control on. An explicit false keeps
 * them hidden even while searching; an explicit true keeps them visible after
 * the search is cleared.
 */
export function queueShowsSnoozed(search: {
  showSnoozed?: boolean;
  search?: string;
}): boolean {
  if (search.showSnoozed === true) return true;
  if (search.showSnoozed === false) return false;
  return Boolean(search.search?.trim());
}

/**
 * Inbox search params cannot store an explicit off. Search includes snoozed
 * tasks until the user hides them for the current query.
 */
export function inboxShowsSnoozed(input: {
  showSnoozed?: boolean;
  search: string;
  hiddenDespiteSearch: boolean;
}): boolean {
  if (input.showSnoozed === true) return true;
  return input.search.trim().length > 0 && !input.hiddenDespiteSearch;
}

/** Banner for a human queue: hidden count, or a way to hide them again. */
export function snoozeQueueNotice(input: {
  showsSnoozed: boolean;
  explicitShow: boolean;
  snoozedCount: number;
  anyVisibleSnoozed: boolean;
}): 'hidden' | 'included' | null {
  if (!input.showsSnoozed && input.snoozedCount > 0) return 'hidden';
  if (input.showsSnoozed && (input.explicitShow || input.anyVisibleSnoozed)) {
    return 'included';
  }
  return null;
}

/** Local wall time for a "Snoozed until …" marker. */
export function formatSnoozedUntil(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, 'MMM d, h:mm a');
}
